import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  OnInit,
  ChangeDetectorRef,
  PLATFORM_ID,
  Inject,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { GoogleMapsModule } from '@angular/google-maps';
import { Sucursal } from '../../types/stores';

@Component({
  selector: 'allas-map-component',
  imports: [CommonModule, GoogleMapsModule],
  template: `
    <div class="relative w-full h-full">
      <google-map
        *ngIf="isGoogleMapsLoaded"
        [center]="mapCenter"
        [zoom]="mapZoom"
        [options]="mapOptions"
        class="w-full h-full"
      >
        <!-- User Location Marker -->
        <map-marker
          *ngIf="userLocation"
          [position]="{ lat: userLocation.lat, lng: userLocation.lon }"
          [options]="userMarkerOptions"
        ></map-marker>

        <!-- Store Markers -->
        <map-marker
          *ngFor="let store of stores"
          [position]="{
            lat: store.coordinates.lat,
            lng: store.coordinates.lng
          }"
          [options]="getMarkerOptions(store)"
          (mapMarkerClick)="onMarkerClick(store)"
        ></map-marker>
      </google-map>

      <!-- Store count indicator -->
      <div
        class="absolute top-2 left-2 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-md text-xs font-medium text-gray-700 shadow-sm z-10"
      >
        {{ stores.length }} tiendas
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        height: 100%;
        width: 100%;
        position: relative;
        z-index: 1;
      }

      ::ng-deep {
        google-map div.map-container {
          height: 100% !important;
          width: 100% !important;
        }
      }
    `,
  ],
})
export class MapComponent implements OnChanges, OnInit {
  @Input() stores: Sucursal[] = [];
  @Input() selectedStore: Sucursal | null = null;
  @Input() nearestStore: Sucursal | null = null;
  @Input() userLocation: { lat: number; lon: number } | null = null;

  @Output() storeSelect = new EventEmitter<Sucursal>();

  isPlatformBrowser = false;
  isGoogleMapsLoaded = false;
  mapCenter: any = { lat: 14.8, lng: -87.0 };
  mapZoom: number = 8;
  mapOptions: any = {
    zoomControl: true,
    scrollwheel: true,
    scaleControl: true,
    streetViewControl: false,
    mapTypeControl: false,
    disableDoubleClickZoom: false,
    mapTypeId: 'roadmap',
    maxZoom: 20,
    minZoom: 6,
    fullscreenControl: true,
    styles: [
      {
        featureType: 'transit',
        stylers: [{ visibility: 'off' }],
      },
      {
        featureType: 'poi.business',
        stylers: [{ visibility: 'off' }],
      },
    ],
  };

  userMarkerOptions: any = {
    icon: {
      url:
        'data:image/svg+xml;charset=UTF-8,' +
        encodeURIComponent(`
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="8" fill="#4285F4" stroke="white" stroke-width="2"/>
          <circle cx="12" cy="12" r="3" fill="white"/>
        </svg>
      `),
      scaledSize: { width: 20, height: 20 },
      anchor: { x: 10, y: 10 },
    },
    title: 'Tu ubicación',
  };

  constructor(
    private cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isPlatformBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    if (this.isPlatformBrowser) {
      this.loadGoogleMaps();
    }
  }

  loadGoogleMaps(): void {
    if (!this.isPlatformBrowser) {
      return;
    }

    // Check if Google Maps is already loaded
    if (
      typeof (window as any).google !== 'undefined' &&
      (window as any).google.maps
    ) {
      this.isGoogleMapsLoaded = true;
      this.cdr.markForCheck();
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector('#google-maps');
    if (existingScript) {
      // If script exists but Google Maps not loaded yet, wait for it
      existingScript.addEventListener('load', () => {
        this.isGoogleMapsLoaded = true;
        this.cdr.markForCheck();
      });
      return;
    }

    // Load the script
    const script = document.createElement('script');
    script.id = 'google-maps';
    script.src =
      'https://maps.googleapis.com/maps/api/js?key=AIzaSyBjInjAYPo9TNT0bLooFj601o3wiyLbxT4&libraries=geometry';
    script.async = true;
    script.defer = true;

    script.onload = () => {
      this.isGoogleMapsLoaded = true;
      this.cdr.markForCheck();
    };

    script.onerror = () => {
      this.isGoogleMapsLoaded = false;
      this.cdr.markForCheck();
    };

    document.body.appendChild(script);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['stores'] && this.stores.length > 0) {
      setTimeout(() => {
        this.fitMapToStores();
        this.cdr.markForCheck();
      }, 0);
    }
    if (changes['selectedStore'] && this.selectedStore) {
      setTimeout(() => {
        if (this.selectedStore) {
          this.centerOnStore(this.selectedStore);
          this.cdr.markForCheck();
        }
      }, 0);
    }
    if (changes['nearestStore'] && this.nearestStore) {
      setTimeout(() => {
        if (this.nearestStore) {
          this.centerOnStore(this.nearestStore);
          this.cdr.markForCheck();
        }
      }, 0);
    }
  }

  getMarkerOptions(store: Sucursal): any {
    // Determine marker style based on store state
    if (this.nearestStore && store.id === this.nearestStore.id) {
      // Nearest store - green marker
      return {
        icon: {
          url:
            'data:image/svg+xml;charset=UTF-8,' +
            encodeURIComponent(`
            <svg width="32" height="42" viewBox="0 0 24 41" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.5 0C19.4036 0 25.5 5.99645 25.5 12.5C25.5 19.4036 19.4036 25.5 12.5 25.5C5.59645 25.5 0 19.4036 0 12.5C0 5.59645 5.59645 0 12.5 0Z" fill="#16A433"/>
              <path d="M12.5 41L12.5 25.5" stroke="#087237" stroke-width="2"/>
            </svg>
          `),
          scaledSize: { width: 32, height: 42 },
          anchor: { x: 12, y: 41 },
        },
        title: `${store.name} - Tienda más cercana`,
      };
    } else if (this.selectedStore && store.id === this.selectedStore.id) {
      // Selected store - blue marker
      return {
        icon: {
          url:
            'data:image/svg+xml;charset=UTF-8,' +
            encodeURIComponent(`
            <svg width="32" height="42" viewBox="0 0 24 41" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.5 0C19.4036 0 25.5 5.99645 25.5 12.5C25.5 19.4036 19.4036 25.5 12.5 25.5C5.59645 25.5 0 19.4036 0 12.5C0 5.59645 5.59645 0 12.5 0Z" fill="#387CE6"/>
              <path d="M12.5 41L12.5 25.5" stroke="#2366FF" stroke-width="2"/>
            </svg>
          `),
          scaledSize: { width: 32, height: 42 },
          anchor: { x: 12, y: 41 },
        },
        title: store.name,
      };
    } else {
      // Default store marker
      return {
        icon: {
          url: './images/mapa/allas-pin.png',
          scaledSize: { width: 25, height: 41 },
          anchor: { x: 12, y: 41 },
        },
        title: store.name,
      };
    }
  }

  onMarkerClick(store: Sucursal): void {
    this.storeSelect.emit(store);
  }

  private fitMapToStores(): void {
    if (this.stores.length === 0) return;

    // Calculate center point from all stores
    let totalLat = 0;
    let totalLng = 0;
    let count = 0;

    this.stores.forEach((store) => {
      totalLat += store.coordinates.lat;
      totalLng += store.coordinates.lng;
      count++;
    });

    // Include user location if available
    if (this.userLocation) {
      totalLat += this.userLocation.lat;
      totalLng += this.userLocation.lon;
      count++;
    }

    // Calculate center
    this.mapCenter = {
      lat: totalLat / count,
      lng: totalLng / count,
    };

    // Calculate bounds to determine zoom
    let minLat = Infinity;
    let maxLat = -Infinity;
    let minLng = Infinity;
    let maxLng = -Infinity;

    this.stores.forEach((store) => {
      minLat = Math.min(minLat, store.coordinates.lat);
      maxLat = Math.max(maxLat, store.coordinates.lat);
      minLng = Math.min(minLng, store.coordinates.lng);
      maxLng = Math.max(maxLng, store.coordinates.lng);
    });

    if (this.userLocation) {
      minLat = Math.min(minLat, this.userLocation.lat);
      maxLat = Math.max(maxLat, this.userLocation.lat);
      minLng = Math.min(minLng, this.userLocation.lon);
      maxLng = Math.max(maxLng, this.userLocation.lon);
    }

    // Calculate appropriate zoom level based on bounds
    const latDiff = maxLat - minLat;
    const lngDiff = maxLng - minLng;
    const maxDiff = Math.max(latDiff, lngDiff);

    if (maxDiff > 5) {
      this.mapZoom = 7;
    } else if (maxDiff > 2) {
      this.mapZoom = 8;
    } else if (maxDiff > 0.5) {
      this.mapZoom = 9;
    } else if (maxDiff > 0.1) {
      this.mapZoom = 10;
    } else {
      this.mapZoom = 12;
    }
  }

  private centerOnStore(store: Sucursal): void {
    this.mapCenter = {
      lat: store.coordinates.lat,
      lng: store.coordinates.lng,
    };
    this.mapZoom = 13;
  }
}
